import type { ClassificationSortField } from "@/domain/classification/types";
import type { IClassificationRepository } from "@/application/ports/repositories";
import type {
  ClassificationResponse,
  ClassificationListResponse,
  ClassificationListParams,
  ClassificationExportParams,
  ClassificationSummaryParams,
  ClassificationSummaryResponse,
  ClassificationTypeCountsParams,
  ClassificationUpdateRequest,
  ClassificationResolveDuplicateRequest,
  ClassificationResolveDuplicateResponse,
  ClassificationBulkDeleteResponse,
  ClassificationBulkReclassifyResponse,
  PipelineStatusBreakdown,
  SuppliersAnalyticsResponse,
  TaxBreakdownAnalyticsResponse,
  WorkforceAnalyticsResponse,
  CreateUploadSessionRequest,
  CreateUploadSessionResponse,
  SignUploadBatchRequest,
  SignUploadBatchResponse,
  UploadSessionStatusResponse,
  ReviewClassificationRequest,
  ClassificationFileResponse,
  ClassificationLocksResponse,
  FieldLockInput,
} from "@/domain/classification";
import type { ExportDownloadOptions, ExportPreview } from "@/domain/export";
import { downloadBlob, downloadFromSignedUrl } from "../fileDownload";
import * as sdk from "../generated/sdk.gen";
import { unwrap, unwrapBlobWithFilename } from "../unwrap";

function normalizeDocumentType<T extends { document_type?: V | V[] }, V>(
  params?: T,
): (Omit<T, "document_type"> & { document_type?: V[] }) | undefined {
  if (!params) return undefined;
  const { document_type, ...rest } = params;
  return {
    ...rest,
    document_type:
      document_type === undefined
        ? undefined
        : Array.isArray(document_type)
          ? document_type
          : [document_type],
  };
}

export class ClassificationRepository implements IClassificationRepository {
  async findAll(
    beneficiaryUuid: string,
    params?: ClassificationListParams,
  ): Promise<ClassificationListResponse> {
    const { items, ...rest } = await unwrap(
      sdk.listClassifications({
        path: { beneficiary_uuid: beneficiaryUuid },
        query: normalizeDocumentType(params),
      }),
    );
    return { classifications: items, ...rest };
  }

  async getTypeCounts(
    beneficiaryUuid: string,
    params?: ClassificationTypeCountsParams,
  ): Promise<Record<string, number>> {
    return unwrap(
      sdk.getClassificationStats({
        path: { beneficiary_uuid: beneficiaryUuid },
        query: normalizeDocumentType(params),
      }),
    );
  }

  async findById(
    beneficiaryUuid: string,
    classificationUuid: string,
  ): Promise<ClassificationResponse> {
    return unwrap(
      sdk.getClassification({
        path: { beneficiary_uuid: beneficiaryUuid, classification_uuid: classificationUuid },
      }),
    );
  }

  async getClassificationFile(
    beneficiaryUuid: string,
    classificationUuid: string,
  ): Promise<ClassificationFileResponse> {
    return unwrap(
      sdk.getClassificationFile({
        path: { beneficiary_uuid: beneficiaryUuid, classification_uuid: classificationUuid },
      }),
    );
  }

  async getSummary(
    beneficiaryUuid: string,
    params?: ClassificationSummaryParams,
  ): Promise<ClassificationSummaryResponse> {
    return unwrap(
      sdk.getClassificationSummary({
        path: { beneficiary_uuid: beneficiaryUuid },
        query: params,
      }),
    );
  }

  async getPipelineStatus(beneficiaryUuid: string): Promise<PipelineStatusBreakdown> {
    return unwrap(
      sdk.getClassificationPipelineStatus({ path: { beneficiary_uuid: beneficiaryUuid } }),
    );
  }

  async getSuppliersAnalytics(
    beneficiaryUuid: string,
    fiscalQuotaUuid?: string,
    expenseType?: string,
  ): Promise<SuppliersAnalyticsResponse> {
    return unwrap(
      sdk.getSuppliersAnalytics({
        path: { beneficiary_uuid: beneficiaryUuid },
        query: { fiscal_quota_uuid: fiscalQuotaUuid, expense_type: expenseType },
      }),
    );
  }

  async getTaxBreakdown(
    beneficiaryUuid: string,
    fiscalQuotaUuid?: string,
    expenseType?: string,
  ): Promise<TaxBreakdownAnalyticsResponse> {
    return unwrap(
      sdk.getTaxBreakdown({
        path: { beneficiary_uuid: beneficiaryUuid },
        query: { fiscal_quota_uuid: fiscalQuotaUuid, expense_type: expenseType },
      }),
    );
  }

  async getWorkforceAnalytics(
    beneficiaryUuid: string,
    fiscalQuotaUuid?: string,
  ): Promise<WorkforceAnalyticsResponse> {
    return unwrap(
      sdk.getWorkforceAnalytics({
        path: { beneficiary_uuid: beneficiaryUuid },
        query: { fiscal_quota_uuid: fiscalQuotaUuid },
      }),
    );
  }

  async createUploadSession(
    beneficiaryUuid: string,
    data: CreateUploadSessionRequest,
  ): Promise<CreateUploadSessionResponse> {
    return unwrap(
      sdk.createUploadSession({
        path: { beneficiary_uuid: beneficiaryUuid },
        body: data,
      }),
    );
  }

  async signUploadBatch(
    beneficiaryUuid: string,
    sessionUuid: string,
    data: SignUploadBatchRequest,
  ): Promise<SignUploadBatchResponse> {
    return unwrap(
      sdk.signUploadBatch({
        path: { beneficiary_uuid: beneficiaryUuid, session_uuid: sessionUuid },
        body: data,
      }),
    );
  }

  async getUploadSessionStatus(
    beneficiaryUuid: string,
    sessionUuid: string,
  ): Promise<UploadSessionStatusResponse> {
    return unwrap(
      sdk.getUploadSessionStatus({
        path: { beneficiary_uuid: beneficiaryUuid, session_uuid: sessionUuid },
      }),
    );
  }

  async updateClassification(
    beneficiaryUuid: string,
    classificationUuid: string,
    data: ClassificationUpdateRequest,
  ): Promise<ClassificationResponse> {
    return unwrap(
      sdk.updateClassification({
        path: { beneficiary_uuid: beneficiaryUuid, classification_uuid: classificationUuid },
        body: data,
      }),
    );
  }

  async reviewClassification(
    beneficiaryUuid: string,
    classificationUuid: string,
    data: ReviewClassificationRequest,
  ): Promise<ClassificationResponse> {
    return unwrap(
      sdk.reviewClassification({
        path: { beneficiary_uuid: beneficiaryUuid, classification_uuid: classificationUuid },
        body: data,
      }),
    );
  }

  async resolveDuplicate(
    beneficiaryUuid: string,
    data: ClassificationResolveDuplicateRequest,
  ): Promise<ClassificationResolveDuplicateResponse> {
    return unwrap(
      sdk.resolveDuplicate({
        path: { beneficiary_uuid: beneficiaryUuid },
        body: data,
      }),
    );
  }

  async deleteClassification(beneficiaryUuid: string, classificationUuid: string): Promise<void> {
    await unwrap(
      sdk.deleteClassification({
        path: { beneficiary_uuid: beneficiaryUuid, classification_uuid: classificationUuid },
      }),
    );
  }

  async bulkDeleteClassifications(
    beneficiaryUuid: string,
    classificationUuids: string[],
  ): Promise<ClassificationBulkDeleteResponse> {
    return unwrap(
      sdk.bulkDeleteClassifications({
        path: { beneficiary_uuid: beneficiaryUuid },
        body: { classification_uuids: classificationUuids },
      }),
    );
  }

  async reclassifyClassification(
    beneficiaryUuid: string,
    classificationUuid: string,
    locks?: FieldLockInput[],
  ): Promise<ClassificationResponse> {
    // Body only when locks are provided: omit to preserve existing sticky
    // locks, send `{ locks }` to replace the set (empty array clears it).
    return unwrap(
      sdk.reclassifyClassification({
        path: { beneficiary_uuid: beneficiaryUuid, classification_uuid: classificationUuid },
        body: locks === undefined ? undefined : { locks },
      }),
    );
  }

  async getClassificationLocks(
    beneficiaryUuid: string,
    classificationUuid: string,
  ): Promise<ClassificationLocksResponse> {
    return unwrap(
      sdk.getClassificationLocks({
        path: { beneficiary_uuid: beneficiaryUuid, classification_uuid: classificationUuid },
      }),
    );
  }

  async bulkReclassifyClassifications(
    beneficiaryUuid: string,
    classificationUuids: string[],
  ): Promise<ClassificationBulkReclassifyResponse> {
    return unwrap(
      sdk.bulkReclassifyClassifications({
        path: { beneficiary_uuid: beneficiaryUuid },
        body: { classification_uuids: classificationUuids },
      }),
    );
  }

  async downloadClassification(
    beneficiaryUuid: string,
    classificationUuid: string,
    filename: string,
  ): Promise<void> {
    const fileResponse = await this.getClassificationFile(beneficiaryUuid, classificationUuid);
    await downloadFromSignedUrl(fileResponse.signed_url, filename);
  }

  async previewExport(params: ClassificationExportParams): Promise<ExportPreview> {
    const { beneficiary_uuid, ...query } = params;
    return unwrap(
      sdk.previewClassificationsExport({
        path: { beneficiary_uuid },
        query,
      }),
    );
  }

  async downloadExport(
    params: ClassificationExportParams,
    options: ExportDownloadOptions,
  ): Promise<void> {
    const { beneficiary_uuid, ...filters } = params;
    const { blob, filename } = await unwrapBlobWithFilename(
      sdk.exportClassifications({
        path: { beneficiary_uuid },
        query: {
          ...filters,
          format: options.format,
          columns: options.columns.length > 0 ? options.columns.join(",") : undefined,
          sort_by: (options.sort_by as ClassificationSortField | undefined) ?? filters.sort_by,
          sort_direction: options.sort_direction ?? filters.sort_direction,
        },
      }),
      `classifications.${options.format}`,
    );
    /**
     * Preset flows (e.g. "Solicitar correcciones al promotor") pass a
     * custom filename so the operator gets a self-describing file name
     * instead of the generic ``classifications_<timestamp>.xlsx`` the
     * backend produces.
     */
    downloadBlob(blob, options.filenameOverride ?? filename);
  }
}
